import {
  AfterViewInit,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthService, BEDOMARE_REMINDER_PENDING_KEY } from '@vinnova/auth';
import { ChatbotService } from '@vinnova/chatbot';
import { EtjansterSites } from '@vinnova/e-tjanster';
import { ModalRef, ModalService } from '@vinnova/ui';
import { filter, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { BedomareHelper } from '../../../core/helpers/bedomare.helper';
import { BedomareQuery } from '../../../core/queries/bedomare.query';

@UntilDestroy()
@Component({
  selector: 'app-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss'],
  standalone: false,
})
export class StartComponent implements OnInit, AfterViewInit {
  isProduction: boolean;
  isBedomare: boolean;
  EtjansterSites = EtjansterSites;
  firstName: string;
  env;
  showChatbot = false;

  removedFromAnsokanModalRef?: ModalRef;
  @ViewChild('removedFromAnsokanModal')
  removedFromAnsokanModal: TemplateRef<unknown> | undefined;

  bedomareReminderModalRef?: ModalRef;
  @ViewChild('bedomareReminderModal')
  bedomareReminderModal: TemplateRef<unknown> | undefined;
  private viewInitialized = false;
  private translationsLoaded = false;

  constructor(
    private authService: AuthService,
    private modalService: ModalService,
    private route: ActivatedRoute,
    private router: Router,
    private translocoService: TranslocoService,
    private chatbotService: ChatbotService,
    private bedomareQuery: BedomareQuery,
  ) {}
  ngOnInit() {
    this.env = environment;
    this.authService
      .getPersonDetails()
      .pipe(take(1))
      .subscribe((details) => {
        this.firstName = details.firstName;
      });
    this.isProduction = environment.production;

    this.authService
      .getIsBedomare()
      .pipe(take(1))
      .subscribe((isBedomare) => {
        this.isBedomare = isBedomare;
        this.tryShowBedomareReminderModal();
      });

    this.translocoService.events$
      .pipe(
        filter((ev) => ev.type === 'translationLoadSuccess'),
        take(1),
      )
      .subscribe(() => {
        this.translationsLoaded = true;
        this.tryShowRemovedFromAnsokanModal();
        this.tryShowBedomareReminderModal();
      });

    this.chatbotService.isAvailable$
      .pipe(untilDestroyed(this))
      .subscribe((isAvailable) => {
        this.showChatbot = isAvailable;
      });
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.tryShowRemovedFromAnsokanModal();
    this.tryShowBedomareReminderModal();
  }

  private tryShowRemovedFromAnsokanModal() {
    if (!this.viewInitialized || !this.translationsLoaded) {
      return;
    }

    this.checkShowRemovedFromAnsokanModal();
  }

  private tryShowBedomareReminderModal() {
    if (!this.viewInitialized || !this.translationsLoaded || !this.isBedomare) {
      return;
    }

    this.checkShowBedomareReminderModal();
  }

  checkShowRemovedFromAnsokanModal() {
    const removed = this.route.snapshot.queryParams.removedFromAnsokan;
    if (!removed || removed !== 'true' || !this.removedFromAnsokanModal) {
      return;
    }

    this.removedFromAnsokanModalRef = this.modalService.show(
      this.removedFromAnsokanModal,
      { backdrop: 'static' },
    );
  }

  checkShowBedomareReminderModal() {
    // Flaggan sätts vid varje ny inloggning (app.component.ts) - konsumeras här så den bara prövas en gång per inloggning
    const pending = sessionStorage.getItem(BEDOMARE_REMINDER_PENDING_KEY);
    sessionStorage.removeItem(BEDOMARE_REMINDER_PENDING_KEY);
    if (!pending) {
      return;
    }

    this.bedomareQuery
      .fetchCurrentBedomare()
      .pipe(take(1))
      .subscribe({
        next: (bedomare) => {
          if (
            bedomare &&
            this.bedomareReminderModal &&
            BedomareHelper.needsConfirmation(bedomare)
          ) {
            this.bedomareReminderModalRef = this.modalService.show(
              this.bedomareReminderModal,
              { backdrop: 'static' },
            );
          }
        },
        error: () =>
          sessionStorage.setItem(BEDOMARE_REMINDER_PENDING_KEY, 'true'),
      });
  }

  closeBedomareReminderModal() {
    this.bedomareReminderModalRef?.hide();
  }

  confirmBedomareProfile() {
    this.bedomareReminderModalRef?.hide();
    this.router.navigate(['/profile']);
  }
}
